import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import { teacherAPI } from '../../services/api';
import './StudentDetail.css';

function StudentDetail({ user, onLogout }) {
  const { studentId } = useParams();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadStudentProgress();
  }, [studentId]);

  const loadStudentProgress = async () => {
    try {
      setLoading(true);
      const response = await teacherAPI.getStudentProgress(studentId);
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load student progress');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="loading">Loading...</div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="error-message">{error}</div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="student-detail">
        <div className="breadcrumb">
          <Link to={`/teacher/dashboard${searchParams.get('group') ? `?group=${encodeURIComponent(searchParams.get('group'))}` : ''}`}>
            Dashboard
          </Link> / {data.student.full_name}
        </div>

        <h1>{data.student.full_name}</h1>
        <div className="student-info">
          <p><strong>Username:</strong> {data.student.username}</p>
          <p><strong>Group:</strong> {data.student.group_name || 'No group'}</p>
        </div>

        {data.progress.map(prog => (
          <details key={prog.exercise.id} className="card exercise-card">
            <summary className="exercise-card-summary">
              <h2>{prog.exercise.title}</h2>
            </summary>

            {!prog.hasAccess ? (
              <p className="no-access">Student does not have access to this exercise</p>
            ) : (
              <>
                <div className="progress-section">
                  <h3>Learning Tasks</h3>
                  {prog.tasks && prog.tasks.length > 0 ? (
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Task</th>
                          <th>Status</th>
                          <th>Test Runs</th>
                          <th>First Passed</th>
                          <th>Last Attempt</th>
                        </tr>
                      </thead>
                      <tbody>
                        {prog.tasks.map(task => (
                          <tr key={task.task_id}>
                            <td>{task.task_id}</td>
                            <td>
                              {task.status === 'passed' && <span className="badge badge-success">Passed</span>}
                              {task.status === 'current' && <span className="badge badge-warning">Current</span>}
                              {task.status === 'locked' && <span className="badge badge-secondary">Locked</span>}
                            </td>
                            <td>{task.test_runs}</td>
                            <td>{task.first_passed_at ? new Date(task.first_passed_at).toLocaleString() : '-'}</td>
                            <td>{task.last_attempt_at ? new Date(task.last_attempt_at).toLocaleString() : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p>Not started yet</p>
                  )}
                </div>

                <div className="progress-section">
                  <h3>Quiz Attempts</h3>
                  {prog.quizAttempts && prog.quizAttempts.length > 0 ? (
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Attempt</th>
                          <th>Score</th>
                          <th>Status</th>
                          <th>Started</th>
                          <th>Submitted</th>
                        </tr>
                      </thead>
                      <tbody>
                        {prog.quizAttempts.map(attempt => (
                          <tr key={attempt.attempt_number}>
                            <td>{attempt.attempt_number}</td>
                            <td>{attempt.score ? `${attempt.score}%` : '-'}</td>
                            <td>
                              {attempt.status === 'submitted' && (
                                <span className={`badge ${attempt.score >= 70 ? 'badge-success' : 'badge-danger'}`}>
                                  {attempt.score >= 70 ? 'Passed' : 'Failed'}
                                </span>
                              )}
                              {attempt.status === 'in_progress' && <span className="badge badge-info">In Progress</span>}
                            </td>
                            <td>{new Date(attempt.started_at).toLocaleString()}</td>
                            <td>{attempt.submitted_at ? new Date(attempt.submitted_at).toLocaleString() : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p>No quiz attempts yet</p>
                  )}
                </div>

                <div className="progress-section">
                  <h3>Current Code</h3>
                  {prog.workspace?.files && Object.keys(prog.workspace.files).length > 0 ? (
                    <div className="student-code-files">
                      {['index.html', 'about.html', 'style.css', 'script.js']
                        .filter(filename => Object.prototype.hasOwnProperty.call(prog.workspace.files, filename))
                        .map(filename => (
                          <details key={filename} className="student-code-file">
                            <summary>{filename}</summary>
                            <pre><code>{prog.workspace.files[filename]}</code></pre>
                          </details>
                        ))}
                      {prog.workspace.last_saved_at && (
                        <p className="code-last-saved">
                          Last saved: {new Date(prog.workspace.last_saved_at).toLocaleString()}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p>No code has been saved yet.</p>
                  )}
                </div>
              </>
            )}
          </details>
        ))}
      </div>
    </Layout>
  );
}

export default StudentDetail;
